/**
 * WashSalesWidget component displaying active wash sale restrictions.
 *
 * Shows a table of securities that cannot be repurchased yet without
 * triggering another wash sale. Rows expiring within 7 days are highlighted.
 */

import { Card, CardHeader, CardTitle, CardContent, Spinner } from '@/components/ui';
import { useWashSales } from '@/hooks/useWashSales';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { calculateDaysRemaining, formatDaysRemaining } from '@/utils/formatters';

interface WashSalesWidgetProps {
  accountId: string | null;
}

export function WashSalesWidget({ accountId }: WashSalesWidgetProps) {
  const { restrictions, isLoading, error, refresh } = useWashSales(accountId);

  return (
    <Card padding="none">
      <CardHeader className="px-6 pt-6 mb-4">
        <CardTitle>
          Wash Sale Restrictions ({restrictions.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        )}

        {!isLoading && error && (
          <div className="px-6 pb-6 text-center">
            <p className="text-rose-400 mb-3">Unable to load wash sale restrictions.</p>
            <button
              onClick={refresh}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-dark-700 text-dark-200 hover:bg-dark-600 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {!isLoading && !error && restrictions.length === 0 && (
          <div className="px-6 pb-6">
            <p className="text-dark-400 text-center py-4">No active wash sale restrictions</p>
          </div>
        )}

        {!isLoading && !error && restrictions.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-700/50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Symbol
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Closing Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Loss
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Avoidance Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Days Left
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-700/30">
                {restrictions.map((restriction) => {
                  const daysRemaining = calculateDaysRemaining(restriction.avoidance_date);
                  const isUrgent = daysRemaining <= 7;

                  return (
                    <tr
                      key={`${restriction.symbol}-${restriction.closing_date}`}
                      className={isUrgent ? 'bg-amber-500/10' : ''}
                    >
                      <td className="px-6 py-3 font-medium text-dark-100">
                        {restriction.symbol}
                      </td>
                      <td className="px-6 py-3 text-dark-300">
                        {formatDate(restriction.closing_date)}
                      </td>
                      <td className="px-6 py-3 text-right font-mono text-rose-400">
                        {formatCurrency(restriction.loss_amount)}
                      </td>
                      <td className="px-6 py-3 text-dark-300">
                        {formatDate(restriction.avoidance_date)}
                      </td>
                      <td className="px-6 py-3 text-right text-dark-300">
                        {formatDaysRemaining(restriction.avoidance_date)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
